import { IBusiness } from './businessTypes';

/**
 * Where the applicant's income comes from. This is the top-level question —
 * it is a separate axis to EEmploymentStatus, which only describes the KIND
 * of work and therefore only applies when the source is WORK.
 */
export enum EIncomeSource {
    WORK = 'WORK',
    GOVERNMENT_PAYMENT = 'GOVERNMENT_PAYMENT',
    NO_INCOME = 'NO_INCOME',
}

export enum EEmploymentStatus {
    /** Labelled "Full-time". Kept as PERMANENT so saved applications and the
     *  Manage mapping (PERMANENT -> FULL_TIME) are unaffected. */
    PERMANENT = 'PERMANENT',
    PART_TIME = 'PART_TIME',
    CASUAL = 'CASUAL',
    /** Benefits as the primary income. Manage accepts this value directly. */
    GOVERNMENT_BENEFITS = 'GOVERNMENT_BENEFITS',
    /** No longer offered. Kept so saved applications still deserialise. */
    OTHER = 'OTHER',
}
export interface IEmploymentState {
    currentEmploymentIndex: number;
    employments: IEmployment[];
}
export interface IEmployment {
    incomeSource?: EIncomeSource | undefined;
    /**
     * Why the status is not a kind of work, in words. The API already reads
     * and persists this (`employmentStatusOtherExplanation`); it is what
     * carries government benefits and no-income through to the Manage note,
     * since neither has a value in Manage's employment status list.
     */
    statusOtherExplanation?: string | undefined;
    status?: EEmploymentStatus | undefined;
    occupation?: string | undefined;
    employerName?: string | undefined;
    employerPhone?: string | undefined;
    years?: number | undefined;
    months?: number | undefined;
    employerLookupDetails?: IBusiness | undefined;
}

export const initialEmployment: IEmployment[] = [];

/**
 * The wording sent as `statusOtherExplanation` for a source Manage's
 * employment status list has no value for. Shared so the screen that writes
 * it and the hydration that reads it back cannot drift apart.
 */
export const NO_INCOME_EXPLANATION = 'No regular income';

/**
 * Works out the income source from what the server actually stores.
 *
 * `incomeSource` is the one answer on this screen with no column behind it —
 * it is the question, not the data, and everything it decides is already
 * persisted somewhere else. Without this, any hydrate (`setBorrower` replaces
 * the whole slice) left the first question blank while every stored answer
 * below it survived, and the kind-of-work reveal disappeared along with it.
 *
 * Derived rather than persisted because it is recoverable in full: a work
 * status means Work, the Manage benefits value means Government benefits,
 * and the note means no income. A new column would only duplicate those.
 */
export function deriveIncomeSource(
    employment: IEmployment | undefined
): EIncomeSource | undefined {
    if (!employment) return undefined;
    if (employment.incomeSource) return employment.incomeSource;
    if (employment.status === EEmploymentStatus.GOVERNMENT_BENEFITS) {
        return EIncomeSource.GOVERNMENT_PAYMENT;
    }
    if (employment.status) return EIncomeSource.WORK;
    if (employment.statusOtherExplanation === NO_INCOME_EXPLANATION) {
        return EIncomeSource.NO_INCOME;
    }
    return undefined;
}
